import { Component, Inject, OnDestroy } from '@angular/core';
import {
	MatDatepickerInputEvent,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
	BehaviorSubject,
	debounce,
	from,
	interval,
	map,
	mergeMap,
	Observable,
	of,
	Subscription,
} from 'rxjs';
import {
	ApiService,
	QueryParam,
	ApiLoadingKeys,
} from 'src/app/services/api.services';
import { MatIconButton, MatButton } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

export type UsageReportFilterOptions =
	Components.Schemas.UsageReportFilterOptions;
interface SearchConfig {
	type: 'deviceOwner';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

export const emptyUsageReportFilters = (): UsageReportFilters => ({
	deviceOwner: [],
	utcStartDate: undefined,
	utcEndDate: undefined,
});

export type UsageReportFilters = {
	deviceOwner?: { id: string; name: string }[];
	utcStartDate?: Date;
	utcEndDate?: Date;
};

@Component({
	selector: 'app-usage-report-dialog',
	templateUrl: './usage-report-dialog.component.html',
	styleUrls: ['./usage-report-dialog.component.scss', '../../common/main.scss'],
	imports: [
		MatIconButton,
		CdkScrollable,
		MatDialogContent,
		LoadingIndicatorComponent,
		MultiSelectTypeaheadComponent,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		MatDialogActions,
		MatButton,
	],
})
export class UsageReportDialogComponent implements OnDestroy {
	usageReportFilters: UsageReportFilters = emptyUsageReportFilters();
	filterOptions?: UsageReportFilterOptions;

	apiLoadingKeys = ApiLoadingKeys;

	queryParams: QueryParam[] = [];

	deviceOwnerOptions?: { id: string; name: string }[] = [];
	deviceOwnerId: string[] = [];

	deviceMultiSelectSearchPlaceholder = $localize`:@@usageReportDialog-deviceMultiSelectSearchPlaceholder: Search Serial Numbers (optional)`;
	deviceOwnerMultiSelectSearchPlaceholder = $localize`:@@usageReportDialog-deviceOwnerMultiSelectSearchPlaceholder: Search Companies (optional)`;
	generatedByMultiSelectSearchPlaceholder = $localize`:@@usageReportDialog-generatedByMultiSelectSearchPlaceholder: Search Users (optional)`;
	userNameOrEmailPlaceHolder = $localize`:@@usageReportDialog-userNameOrEmailPlaceHolder: User name or email`;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		deviceOwner: {
			type: 'deviceOwner',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOwnerOptions || [],
			apiCall: (params) => this.apiService.getCompanyFilterOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.usageReportFilters.deviceOwner = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<UsageReportDialogComponent>,
		private readonly apiService: ApiService,
		private readonly router: Router,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters: UsageReportFilters;
			filterOptions: UsageReportFilterOptions;
		}
	) {
		this.filterOptions = data?.filterOptions;
		this.usageReportFilters = data?.reportFilters ?? emptyUsageReportFilters();
		if (data?.reportFilters) {
			this.usageReportFilters.deviceOwner?.forEach((deviceOwner) => {
				this.deviceOwnerId.push(deviceOwner.id);
			});
		}

		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
	}

	get hasFilters(): boolean {
		let hasFilters =
			JSON.stringify(this.usageReportFilters) !==
			JSON.stringify(emptyUsageReportFilters());

		return hasFilters;
	}

	get primaryButtonText(): string {
		return this.hasFilters
			? $localize`:@@usageReportDialog-generate:Generate`
			: $localize`:@@usageReportDialog-generateWithoutFilters:Generate without Filters`;
	}

	onSelectedDeviceOwnerEvent(deviceOwner: any): void {
		if (deviceOwner === undefined) {
			return;
		}
		this.usageReportFilters.deviceOwner = [...deviceOwner];
	}

	onDateValidStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.usageReportFilters.utcStartDate = event.value ?? undefined;
	}

	onDateValidEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.usageReportFilters.utcEndDate = event.value ?? undefined;
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	async onGenerateReport(): Promise<void> {
		const compiledFilters = this.compileFilters();

		this.router
			.navigateByUrl(`/reports/usage-report-table`, {
				state: {
					reportFilters: this.usageReportFilters,
					compiledFilters,
					filterOptions: this.filterOptions,
					hasFilters: this.hasFilters,
				},
			})
			.then(() => {
				this.dialogRef.close();
			});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		if (
			this.usageReportFilters.deviceOwner &&
			this.usageReportFilters.deviceOwner.length > 0
		) {
			queryParameters.push({
				param: 'deviceOwnerCompanies',
				value: this.usageReportFilters.deviceOwner.map((it) => it.id),
			});
		}

		if (this.usageReportFilters.utcStartDate) {
			queryParameters.push({
				param: 'startDate',
				value: this.usageReportFilters.utcStartDate.toISOString().split('T')[0],
			});
		}

		if (this.usageReportFilters.utcEndDate) {
			queryParameters.push({
				param: 'endDate',
				value: this.usageReportFilters.utcEndDate.toISOString().split('T')[0],
			});
		}

		return queryParameters;
	}
	private initializeData(): void {
		this.filterOptions = this.data?.filterOptions;

		if (this.data?.reportFilters) {
			this.usageReportFilters = this.data.reportFilters;

			// Initialize deviceOwnerOptions with previous selections
			if (this.usageReportFilters.deviceOwner?.length) {
				this.usageReportFilters.deviceOwner.forEach((company) => {
					const companyOption = {
						id: company.id,
						name: company.name,
					};
					this.deviceOwnerId.push(company.id);
					this.searchConfigs.deviceOwner.selectedItems.add(companyOption);
				});
				// Initialize deviceOwnerOptions with selected companies
				this.deviceOwnerOptions = Array.from(
					this.searchConfigs.deviceOwner.selectedItems
				);
			}
		}
	}

	private setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	private handleSearch(
		searchTerm: string,
		config: SearchConfig
	): Observable<any[]> {
		if (!searchTerm || searchTerm === '' || searchTerm.length < 3) {
			switch (config.type) {
				case 'deviceOwner':
					return of(this.deviceOwnerOptions || []);
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => {
				if (!results?.length) {
					return Array.from(config.selectedItems);
				}

				const newOptions = results.map(config.mapResponse);
				const mergedOptions = this.mergeWithSelected(
					newOptions,
					config.selectedItems
				);

				// Update the corresponding options array based on the config type
				switch (config.type) {
					case 'deviceOwner':
						this.deviceOwnerOptions = mergedOptions;
						break;
				}

				return mergedOptions;
			})
		);
	}
	private getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'deviceOwner':
				return 'companyName';
			default:
				return 'name';
		}
	}

	private mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
		});
	}
}
